import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Building2, Zap, DollarSign, Loader2, RefreshCw, TrendingUp,
  Shield, Calendar, FileText, Trash2, Activity, PlayCircle, CalendarPlus,
  Mail, Megaphone, Ban, Plug, CheckCircle2, AlertCircle, XCircle,
} from "lucide-react";

const brl = (v) => (Number(v || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [extending, setExtending] = useState(null); // account being extended
  const [managingBlings, setManagingBlings] = useState(null); // account whose Blings are being managed
  const [opsBusy, setOpsBusy] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, a, h] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/accounts"),
        api.get("/admin/system-health"),
      ]);
      setStats(s.data);
      setAccounts(a.data);
      setHealth(h.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  if (user && user.role !== "platform_admin") {
    return <Navigate to="/app" replace />;
  }

  const saveEdit = async (patch) => {
    try {
      await api.put(`/admin/accounts/${editing.id}`, patch);
      toast.success("Conta atualizada");
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro");
    }
  };

  const deleteAccount = async (accId) => {
    if (!window.confirm(`ATENÇÃO: excluir esta conta apagará TODOS os dados dela (pedidos, integrações, etc.). Continuar?`)) return;
    try {
      await api.delete(`/admin/accounts/${accId}`);
      toast.success("Conta excluída");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro");
    }
  };

  const extendTrial = async (acc, days) => {
    try {
      await api.post(`/admin/accounts/${acc.id}/extend-trial`, { days });
      toast.success(`Trial de ${acc.name} estendido em ${days} dia${days > 1 ? "s" : ""}`);
      setExtending(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro");
    }
  };

  const runOp = async (op, endpoint, successMsg) => {
    setOpsBusy(op);
    try {
      await api.post(endpoint);
      toast.success(successMsg);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setOpsBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Shield className="w-3 h-3" /> Painel Administrador · Vyntric
          </div>
          <h1 className="text-3xl font-black tracking-tighter">Contas &amp; Assinaturas</h1>
        </div>
        <button
          data-testid="admin-refresh"
          onClick={fetchAll}
          className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Atualizar
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="admin-stats">
          <KpiCard icon={Building2} label="Contas" value={stats.total_accounts} accent="slate" />
          <KpiCard icon={TrendingUp} label="Ativas" value={stats.active_accounts} accent="emerald" />
          <KpiCard icon={Calendar} label="Em Trial" value={stats.trial_accounts} accent="amber" />
          <KpiCard icon={Users} label="Usuários" value={stats.total_users} accent="slate" />
          <KpiCard icon={Zap} label="Blings" value={stats.total_blings} accent="blue" />
          <KpiCard icon={DollarSign} label="MRR" value={brl(stats.mrr_brl)} accent="emerald" small />
        </div>
      )}

      {/* System health + quick ops */}
      {health && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="admin-health">
          <div className="border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1.5">
              <Activity className="w-3 h-3" /> Saúde do sistema
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">NF-e presas</span><span className={`font-bold font-mono ${health.invoice_failures.stuck_needs_manual > 0 ? "text-red-600" : "text-emerald-600"}`}>{health.invoice_failures.stuck_needs_manual}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Reprocessando</span><span className="font-bold font-mono">{health.invoice_failures.retrying}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Suporte pendente</span><span className={`font-bold font-mono ${health.support.awaiting_admin > 0 ? "text-amber-600" : "text-emerald-600"}`}>{health.support.awaiting_admin}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Contas ativas</span><span className="font-bold font-mono">{health.accounts_by_status.active || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Trials</span><span className="font-bold font-mono">{health.accounts_by_status.trial || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Zpl histórico</span><span className="font-bold font-mono">{health.counts.zpl_history || 0}</span></div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-1 text-[11px]">
              <span className={`px-2 py-0.5 border ${health.email_configured ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-red-300 text-red-700 bg-red-50"}`}>
                Resend: {health.email_configured ? "OK" : "OFF"}
              </span>
              <span className={`px-2 py-0.5 border ${health.asaas_configured ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-red-300 text-red-700 bg-red-50"}`}>
                Asaas: {health.asaas_configured ? "OK" : "OFF"}
              </span>
            </div>
          </div>

          <div className="border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1.5">
              <PlayCircle className="w-3 h-3" /> Ações rápidas
            </div>
            <div className="mt-2 space-y-1.5">
              <button
                type="button"
                data-testid="admin-op-retry-invoices"
                disabled={opsBusy === "retry"}
                onClick={() => runOp("retry", "/admin/ops/retry-invoices-now", "Reprocessamento de NF-e disparado")}
                className="w-full px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-[#002EA6] hover:text-[#002EA6] flex items-center justify-between disabled:opacity-60"
              >
                <span>Reprocessar NF-e presas</span>
                {opsBusy === "retry" ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
              </button>
              <button
                type="button"
                data-testid="admin-op-trial-reminders"
                disabled={opsBusy === "trials"}
                onClick={() => runOp("trials", "/admin/ops/trial-reminders-now", "Lembretes de trial disparados")}
                className="w-full px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-[#002EA6] hover:text-[#002EA6] flex items-center justify-between disabled:opacity-60"
              >
                <span>Enviar lembretes de trial</span>
                {opsBusy === "trials" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              </button>
              <Link
                to="/admin/announcements"
                data-testid="admin-op-announcements"
                className="w-full px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-[#002EA6] hover:text-[#002EA6] flex items-center justify-between"
              >
                <span>Publicar aviso aos usuários</span>
                <Megaphone className="w-3.5 h-3.5" />
              </Link>
              <Link
                to="/admin/support"
                data-testid="admin-op-support"
                className="w-full px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-[#002EA6] hover:text-[#002EA6] flex items-center justify-between"
              >
                <span>Central de Suporte</span>
                <span className="text-amber-600 font-mono">{health.support.awaiting_admin}</span>
              </Link>
            </div>
          </div>

          <div className="border border-slate-200 bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1.5">
              <Calendar className="w-3 h-3" /> Jobs agendados
            </div>
            <div className="mt-2 space-y-1 text-[11px] font-mono">
              {(health.scheduler_jobs || []).slice(0, 8).map((j) => (
                <div key={j.id} className="flex items-center justify-between gap-2">
                  <span className="truncate text-slate-600" title={j.id}>{j.id}</span>
                  <span className="text-slate-400 shrink-0">
                    {j.next_run_time ? new Date(j.next_run_time).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-xl overflow-hidden" data-testid="admin-accounts-table">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/60 text-sm font-black uppercase tracking-widest text-slate-700">
          Contas cadastradas
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Carregando...
          </div>
        ) : accounts.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Nenhuma conta ainda.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr className="text-[10px] uppercase tracking-widest text-slate-600">
                <th className="px-3 py-2 text-left">Empresa</th>
                <th className="px-3 py-2 text-left">Dono</th>
                <th className="px-3 py-2 text-center">Usuários</th>
                <th className="px-3 py-2 text-center">Blings</th>
                <th className="px-3 py-2 text-center">NF-e</th>
                <th className="px-3 py-2 text-left">Plano</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {accounts.map((acc) => {
                const p = acc.plan || {};
                // Priority: LIFETIME > paid > trial > expired. Once a
                // customer is paying, we must not show TRIAL even if
                // the original 7-day window hasn't naturally expired.
                const statusColor = p.is_lifetime
                  ? "purple"
                  : p.is_paid
                    ? "emerald"
                    : p.trial_active
                      ? "amber"
                      : "red";
                const statusLabel = p.is_lifetime
                  ? "LIFETIME"
                  : p.is_paid
                    ? "ATIVO"
                    : p.trial_active
                      ? "TRIAL"
                      : "EXPIRADO";
                return (
                  <tr key={acc.id} className="hover:bg-slate-50">
                    <td className="px-3 py-3 font-bold">{acc.name}</td>
                    <td className="px-3 py-3 text-xs text-slate-600 font-mono">{acc.owner_email || "—"}</td>
                    <td className="px-3 py-3 text-center font-mono">{acc.user_count}</td>
                    <td className="px-3 py-3 text-center font-mono">{acc.bling_count}/{p.bling_limit}</td>
                    <td className="px-3 py-3 text-center font-mono">{acc.invoiced_total}</td>
                    <td className="px-3 py-3 text-xs">
                      <div className="font-bold">{brl(p.monthly_total_brl)}/mês</div>
                      <div className="text-slate-500">{p.plan_code}</div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-sm bg-${statusColor}-100 text-${statusColor}-800`}>
                        {statusLabel}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`admin-manage-blings-${acc.id}`}
                          onClick={() => setManagingBlings(acc)}
                          className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-sm inline-flex items-center gap-1"
                          title="Ver Blings desta conta e cancelar cobrança de algum"
                        >
                          <Zap className="w-3 h-3" /> Blings
                        </button>
                        {(p.trial_active || !p.is_paid) && !p.is_lifetime && (
                          <button
                            data-testid={`admin-extend-trial-${acc.id}`}
                            onClick={() => setExtending(acc)}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-sm inline-flex items-center gap-1"
                            title="Estender trial"
                          >
                            <CalendarPlus className="w-3 h-3" /> +Trial
                          </button>
                        )}
                        <button
                          data-testid={`admin-edit-account-${acc.id}`}
                          onClick={() => setEditing(acc)}
                          className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-slate-300 bg-white hover:bg-slate-50 rounded-sm"
                        >
                          Editar
                        </button>
                        {acc.id !== "system-account" && (
                          <button
                            data-testid={`admin-delete-account-${acc.id}`}
                            onClick={() => deleteAccount(acc.id)}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-red-300 bg-red-50 hover:bg-red-100 text-red-800 rounded-sm inline-flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <EditAccountDialog
          account={editing}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}

      {extending && (
        <ExtendTrialDialog
          account={extending}
          onClose={() => setExtending(null)}
          onConfirm={(days) => extendTrial(extending, days)}
        />
      )}

      {managingBlings && (
        <ManageBlingsDialog
          account={managingBlings}
          onClose={() => setManagingBlings(null)}
          onDone={() => { setManagingBlings(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

function ExtendTrialDialog({ account, onClose, onConfirm }) {
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    await onConfirm(days);
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" data-testid="extend-trial-dialog" onClick={onClose}>
      <div className="bg-white border border-slate-200 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Estender trial</div>
        <h3 className="text-xl font-black tracking-tight mt-1">{account.name}</h3>
        <p className="text-xs text-slate-600 mt-1">
          Adiciona dias ao período de trial. Se já expirou, começa a contar de agora.
        </p>
        <label className="block mt-4">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Quantos dias?</span>
          <input
            type="number"
            data-testid="extend-trial-days"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value || "1", 10))))}
            className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002EA6]"
          />
        </label>
        <div className="mt-3 flex flex-wrap gap-1 text-[11px]">
          {[3, 7, 14, 30].map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)}
                    className={`px-2 py-1 border ${days === d ? "border-[#002EA6] text-[#002EA6]" : "border-slate-300 text-slate-600 hover:border-slate-900"}`}>
              +{d}d
            </button>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 hover:border-slate-900">Cancelar</button>
          <button
            type="button"
            data-testid="extend-trial-confirm"
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 text-xs font-bold uppercase bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarPlus className="w-3 h-3" />}
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent = "slate", small = false }) {
  const colors = {
    slate: "text-slate-600 bg-slate-100",
    emerald: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
    blue: "text-blue-700 bg-blue-50",
  };
  return (
    <div className="border border-slate-200 bg-white rounded-lg p-3">
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[9px] uppercase tracking-widest font-black rounded-sm ${colors[accent]}`}>
        <Icon className="w-2.5 h-2.5" /> {label}
      </div>
      <div className={`mt-1 font-black tracking-tighter text-slate-900 ${small ? "text-lg" : "text-2xl"}`}>{value}</div>
    </div>
  );
}

function EditAccountDialog({ account, onClose, onSave }) {
  const p = account.plan || {};
  const [blingLimit, setBlingLimit] = useState(p.bling_limit || 1);
  const [status, setStatus] = useState(account.subscription_status || "trial");
  const [paidUntil, setPaidUntil] = useState(account.paid_until ? account.paid_until.slice(0, 10) : "");
  const [lifetime, setLifetime] = useState(!!account.lifetime);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-md shadow-2xl border border-slate-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-sm font-black uppercase tracking-widest">Editar Conta</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm">
            <b>{account.name}</b>
            <div className="text-xs text-slate-500 font-mono">{account.owner_email}</div>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">Limite de Blings</span>
            <input
              type="number" min="1" max="99"
              value={blingLimit}
              onChange={(e) => setBlingLimit(Number(e.target.value))}
              className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm">
              <option value="trial">Trial</option>
              <option value="active">Ativo (pago)</option>
              <option value="expired">Expirado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">Pago até</span>
            <input
              type="date"
              value={paidUntil}
              onChange={(e) => setPaidUntil(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm"
            />
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={lifetime} onChange={(e) => setLifetime(e.target.checked)} />
            <span className="text-sm">Acesso vitalício (nunca expira)</span>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold uppercase border border-slate-300 bg-white rounded-sm">Cancelar</button>
          <button
            data-testid="admin-edit-save"
            onClick={() => onSave({
              bling_limit: blingLimit,
              subscription_status: status,
              paid_until: paidUntil ? new Date(paidUntil).toISOString() : null,
              lifetime: lifetime,
            })}
            className="px-4 py-2 text-xs font-bold uppercase bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}


const STATUS_META = {
  connected: { label: "Conectado", color: "text-emerald-700 bg-emerald-50 border-emerald-300", icon: CheckCircle2 },
  disconnected: { label: "Desconectado", color: "text-slate-600 bg-slate-100 border-slate-300", icon: Plug },
  missing_credentials: { label: "Sem credenciais", color: "text-red-700 bg-red-50 border-red-300", icon: AlertCircle },
  token_expired: { label: "Token expirado", color: "text-amber-700 bg-amber-50 border-amber-300", icon: AlertCircle },
  error: { label: "Erro", color: "text-red-700 bg-red-50 border-red-300", icon: AlertCircle },
  cancelled: { label: "Cobrança cancelada", color: "text-red-700 bg-red-50 border-red-300", icon: Ban },
};

function ManageBlingsDialog({ account, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null); // integration id
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/accounts/${account.id}/integrations`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao carregar Blings");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [account.id]);

  const runCancel = async (integ) => {
    setCancelling(integ.id);
    try {
      const { data: resp } = await api.post(
        `/admin/integrations/${integ.id}/cancel-billing`,
        { reason: (reason || "").trim() || null },
      );
      if (resp?.billing_synced === false && resp?.stripe_error) {
        toast.warning(
          `Bling ${integ.nickname || integ.name} marcado como cancelado, mas Stripe retornou: ${resp.stripe_error}`,
          { duration: 10000 },
        );
      } else {
        toast.success(
          `Cobrança do Bling "${integ.nickname || integ.name}" cancelada. ` +
          `Novo limite: ${resp.new_bling_limit} — ${resp.new_extras_qty} adicional(is) no Stripe.`,
          { duration: 8000 },
        );
      }
      setReason("");
      await load();
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao cancelar cobrança");
    } finally {
      setCancelling(null);
    }
  };

  const brlFmt = (v) => (Number(v || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const plan = data?.account?.plan || {};
  const canCancel = (plan.bling_limit || 1) > 1;
  const unusedSlots = data?.account?.unused_slots || 0;
  const [cancelUnusedBusy, setCancelUnusedBusy] = useState(false);

  const runCancelUnusedSlot = async () => {
    if (!window.confirm(
      `Cancelar 1 slot Bling pago mas sem uso?\n\n` +
      `- bling_limit: ${plan.bling_limit} → ${plan.bling_limit - 1}\n` +
      `- extras no Stripe: ${plan.extra_blings || 0} → ${Math.max(0, (plan.extra_blings || 0) - 1)}\n` +
      `- Nenhuma integração conectada será afetada\n` +
      `- Próxima fatura não incluirá este slot`
    )) return;
    setCancelUnusedBusy(true);
    try {
      const { data: resp } = await api.post(
        `/admin/accounts/${account.id}/cancel-unused-slot`,
        { reason: (reason || "").trim() || null },
      );
      if (resp?.billing_synced === false && resp?.stripe_error) {
        toast.warning(
          `Slot ocioso cancelado localmente. Stripe retornou: ${resp.stripe_error}`,
          { duration: 10000 },
        );
      } else {
        toast.success(
          `Slot ocioso cancelado. Novo limite: ${resp.new_bling_limit} — ` +
          `${resp.new_extras_qty} adicional(is) no Stripe.`,
          { duration: 8000 },
        );
      }
      setReason("");
      await load();
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao cancelar slot ocioso");
    } finally {
      setCancelUnusedBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="manage-blings-dialog"
    >
      <div
        className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl border-2 border-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 bg-slate-900 text-white">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            <div>
              <h2 className="font-black uppercase tracking-wider text-sm">
                Blings da conta
              </h2>
              <div className="text-xs text-white/70 mt-0.5">{account.name} · {account.owner_email}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-xl leading-none"
            data-testid="manage-blings-close"
          >×</button>
        </div>

        <div className="p-5 space-y-4">
          {loading || !data ? (
            <div className="py-8 text-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Carregando...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="border border-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Limite</div>
                  <div className="font-black text-lg text-slate-900">{plan.bling_limit}</div>
                </div>
                <div className="border border-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Extras (Stripe)</div>
                  <div className="font-black text-lg text-slate-900">{plan.extra_blings || 0}</div>
                </div>
                <div className="border border-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Mensal atual</div>
                  <div className="font-black text-sm text-slate-900">{brlFmt(plan.monthly_total_brl)}</div>
                </div>
                <div className="border border-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Status</div>
                  <div className="font-black text-xs text-slate-900 uppercase">{plan.status || "?"}</div>
                </div>
              </div>

              {!canCancel && (
                <div className="border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    Essa conta tem apenas <b>{plan.bling_limit} Bling</b> (o incluso no plano). Para cancelar o único
                    Bling, o cliente precisa cancelar o plano inteiro em <i>Minha Conta</i>.
                  </div>
                </div>
              )}

              {canCancel && unusedSlots > 0 && (
                <div
                  className="border-2 border-orange-400 bg-orange-50 p-3 flex items-start gap-3"
                  data-testid="unused-slot-banner"
                >
                  <AlertCircle className="w-5 h-5 shrink-0 text-orange-700 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-orange-900">
                      {unusedSlots} slot{unusedSlots > 1 ? "s" : ""} pago{unusedSlots > 1 ? "s" : ""} SEM USO
                    </div>
                    <div className="text-xs text-orange-900 mt-1">
                      A conta tem <b>{plan.bling_limit}</b> slot{plan.bling_limit > 1 ? "s" : ""} contratado{plan.bling_limit > 1 ? "s" : ""} mas só
                      {" "}<b>{data.account.used_slots}</b> Bling{data.account.used_slots !== 1 ? "s" : ""} conectado{data.account.used_slots !== 1 ? "s" : ""}.
                      O cliente está pagando {brlFmt(unusedSlots * (plan.extra_bling_brl || 49.9))}/mês sem usar.
                    </div>
                    <button
                      data-testid="admin-cancel-unused-slot"
                      onClick={runCancelUnusedSlot}
                      disabled={cancelUnusedBusy}
                      className="mt-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-orange-600 hover:bg-orange-700 text-white rounded-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {cancelUnusedBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                      Cancelar 1 slot ocioso
                    </button>
                  </div>
                </div>
              )}

              {canCancel && (
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-widest font-bold mb-1 text-slate-600">
                    Motivo (registrado no log de auditoria)
                  </span>
                  <input
                    type="text"
                    data-testid="manage-blings-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={400}
                    placeholder="Ex.: Cliente comprou extra por engano · Ticket #123"
                    className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-red-600"
                  />
                </label>
              )}

              <div className="border border-slate-200">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest font-bold text-slate-600">
                  Integrações Bling ({data.integrations.length})
                </div>
                {data.integrations.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500 italic">Nenhuma integração Bling.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Apelido</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">ID Bling</th>
                        <th className="px-3 py-2 text-right">Ação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.integrations.map((i) => {
                        const meta = STATUS_META[i.status] || STATUS_META.disconnected;
                        const Icon = meta.icon;
                        const isCancelled = i.status === "cancelled";
                        return (
                          <tr key={i.id} data-testid={`admin-bling-row-${i.id}`}>
                            <td className="px-3 py-2">
                              <div className="font-bold">{i.nickname || i.name || "(sem nome)"}</div>
                              {i.account_label && (
                                <div className="text-[10px] font-mono text-slate-500">{i.account_label}</div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${meta.color}`}>
                                <Icon className="w-3 h-3" /> {meta.label}
                              </span>
                              {isCancelled && i.cancelled_at && (
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  em {new Date(i.cancelled_at).toLocaleString("pt-BR")}
                                  {i.cancelled_by && <> · por {i.cancelled_by}</>}
                                </div>
                              )}
                              {isCancelled && i.cancel_reason && (
                                <div className="text-[10px] text-slate-600 italic mt-0.5">"{i.cancel_reason}"</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-[10px] font-mono text-slate-500">{i.id}</td>
                            <td className="px-3 py-2 text-right">
                              {isCancelled ? (
                                <span className="text-[10px] text-slate-400 italic">já cancelado</span>
                              ) : (
                                <button
                                  data-testid={`admin-cancel-bling-${i.id}`}
                                  disabled={!canCancel || cancelling === i.id}
                                  onClick={() => {
                                    if (!window.confirm(
                                      `Cancelar cobrança do Bling "${i.nickname || i.name}"?\n\n` +
                                      `- bling_limit: ${plan.bling_limit} → ${plan.bling_limit - 1}\n` +
                                      `- extras no Stripe: ${plan.extra_blings || 0} → ${Math.max(0, (plan.extra_blings || 0) - 1)}\n` +
                                      `- Dados do Bling são preservados\n` +
                                      `- Próxima fatura não incluirá este slot`
                                    )) return;
                                    runCancel(i);
                                  }}
                                  className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-red-300 bg-red-50 hover:bg-red-100 text-red-800 rounded-sm inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  {cancelling === i.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <XCircle className="w-3 h-3" />
                                  )}
                                  Cancelar cobrança
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
                Cancelar aqui reduz o <b>bling_limit</b> em 1 e sincroniza a assinatura Stripe imediatamente
                (Stripe cria proração automática). Dados do Bling ficam preservados para reativação futura.
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase border border-slate-300 bg-white hover:bg-slate-50"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
